import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { UIStore } from '../../../core/services/ui.store';
import {
  AssistantContext,
  AssistantPendingAction,
  AssistantResolveRequest,
  VoiceAssistantLanguage,
  VoiceAssistantMode
} from '../../../core/services/voice-actions/models';
import { VoiceFlowContextService } from '../../../core/services/voice-actions/flows/voice-flow-context.service';
import { GlobalNavigationVoiceFlowService } from '../navigation/global-navigation-voice-flow.service';

type QueryValue = string | number | boolean;

export interface VoiceAssistantRequestOptions {
  mode?: VoiceAssistantMode;
  includeNavigation?: boolean;
  languageOverride?: VoiceAssistantLanguage;
}

@Injectable({
  providedIn: 'root'
})
export class VoiceAssistantRequestContextService {
  private readonly router = inject(Router);
  private readonly uiStore = inject(UIStore);
  private readonly voiceFlowContext = inject(VoiceFlowContextService);
  private readonly globalNavigationVoiceFlow = inject(GlobalNavigationVoiceFlowService);

  buildRequest(
    message: string,
    pendingAction: AssistantPendingAction | null,
    options: VoiceAssistantRequestOptions = {}
  ): AssistantResolveRequest {
    const includeNavigation = options.includeNavigation !== false;
    if (includeNavigation) {
      this.globalNavigationVoiceFlow.publishForCurrentUser();
    }

    const urlTree = this.router.parseUrl(this.router.url);
    const queryParams: Record<string, QueryValue> = {};

    Object.entries(urlTree.queryParams).forEach(([key, value]) => {
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        queryParams[key] = value;
      }
    });

    const segments = urlTree.root.children['primary']?.segments.map((segment) => segment.path) || [];
    const routePath = `/${segments.join('/') || 'home'}`;
    const context: AssistantContext = {
      currentPath: this.router.url,
      routePath,
      queryParams,
      mode: options.mode ?? 'work',
      pendingAction: includeNavigation ? pendingAction : null
    };

    if (includeNavigation) {
      context.flowContext = this.voiceFlowContext.snapshotForRoute(routePath);
    }

    return {
      message,
      route: this.router.url,
      language: options.languageOverride ?? (this.uiStore.brainLanguage() === 'ur' ? 'hi' : 'en'),
      context
    };
  }
}
